import React from "react";

export interface DemographicsData {
  status: string;
  role: string;
  region: string;
  tier: string;
  hoursRequired: string;
  totalHoursRequired?: string | number | null;
  bonusIneligible: boolean;
  salary?: number | null;
  analystLevel?: string | null;
  startDate?: string | null;
  rehireDate?: string | null;
  statusChange?: string | null;
  anniversaryDate?: string | null;
}

interface Props {
  title: string;
  data?: DemographicsData;
  loading: boolean;
  finalization: boolean;
  display: { EmployeeInfo: boolean; TotalHoursRequired: boolean };
}

const formatDate = (value?: string | null) =>
  value ? new Date(value).toLocaleDateString() : null;

const formatSalary = (salary?: number | null) =>
  salary ? `$${salary.toLocaleString()}` : null;

// The API sends numbers here, so a plain `value &&` guard would render a bare 0.
const hasValue = (value?: string | number | null) =>
  value !== null && value !== undefined && value !== "";


const cardBase =
  "bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-md p-4";

const DemographicsCard: React.FC<Props> = ({ title, data, loading, finalization, display }) => {
  if (loading) {
    return (
      <div className={cardBase}>
        <h3 className="font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
        <p className="mt-2 text-gray-600 dark:text-gray-400">Loading Employee Information...</p>
      </div>
    );
  }

  if (finalization) {
    return (
      <div className={cardBase}>
        <p className="text-gray-600 dark:text-gray-400">Employee Data is Updating...</p>
      </div>
    );
  }

  if (!data || !data.status) {
    return (
      <div className={cardBase}>
        <p className="text-gray-600 dark:text-gray-400">No Employee Data Found</p>
      </div>
    );
  }

  return (
    <div className={`${cardBase} grid gap-2`}>
      <h3 className="font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
      {display.EmployeeInfo && (
        <>
          <p className="text-gray-800 dark:text-gray-200">Employee Status: {data.status}</p>
          {data.statusChange && <p className="text-gray-700 dark:text-gray-300">Status Change: {formatDate(data.statusChange)}</p>}
          <p className="text-gray-800 dark:text-gray-200">Role: {data.role}</p>
          {data.analystLevel && <p className="text-gray-700 dark:text-gray-300">Analyst Level: {data.analystLevel}</p>}
          <p className="text-gray-800 dark:text-gray-200">Region: {data.region}</p>
          {formatSalary(data.salary) && <p className="text-gray-700 dark:text-gray-300">Salary: {formatSalary(data.salary)}</p>}
          <p className="text-gray-800 dark:text-gray-200">Hours Required Per Week: {parseInt(data.hoursRequired)}</p>
          {data.startDate && <p className="text-gray-700 dark:text-gray-300">Start Date: {formatDate(data.startDate)}</p>}
          {data.rehireDate && <p className="text-gray-700 dark:text-gray-300">Rehire Date: {formatDate(data.rehireDate)}</p>}
          {data.anniversaryDate && <p className="text-gray-700 dark:text-gray-300">Anniversary Date: {formatDate(data.anniversaryDate)}</p>}
        </>
      )}
      {display.TotalHoursRequired && hasValue(data.totalHoursRequired) && (
        <p className="text-gray-700 dark:text-gray-300">Total Hours Required This Quarter: {data.totalHoursRequired}</p>
      )}
    </div>
  );
};

export default DemographicsCard;